import { Alert, Button, Stack } from "@mui/material";
import { Form, Formik } from "formik";
import { FormikHelpers } from "formik/dist/types";
import React, { FC, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useIdentityDao } from "../dao/useIdentityDao";
import { LoginState } from "../LoginState";
import { SizeClass, usePaneWidth } from "shared-ts-ui-responsive";
import { LoginFooter } from "./LoginFooter";
import { LoginFormValues } from "./LoginFormValues";
import { LoginStrategy } from "./LoginStrategy";
import { useResolveLoginStrategy } from "./useResolveLoginStrategy";
import { PropertiesCard } from "shared-ts-ui-properties-card";
import { FormikProperty } from "shared-ts-property";

export interface LoginFormUsingApiProps {
  username?: string | null;
  loginState: LoginState | undefined;
  strategy?: LoginStrategy; // defaults to 'auto'
  returnUrl?: string; // defaults to current location
  onLoginStateChange: (result: LoginState) => void;
  onRegisterNewUser: () => void;
}

export const LoginFormUsingApi: FC<LoginFormUsingApiProps> = (props) => {
  const width = usePaneWidth();

  const { username, loginState, onLoginStateChange, onRegisterNewUser } = props;
  const { t } = useTranslation("shared-ts-ui-identity");

  const [sendResetPasswordStatus, setSendResetPasswordStatus] = useState<
    "none" | "sending" | "sent"
  >("none");

  const identityDao = useIdentityDao();
  const strategy = useResolveLoginStrategy(props.strategy);

  const initialValues: LoginFormValues = {
    username: username ?? "",
    password: "",
  };

  const handleNewUserClick = useCallback(
    () => onRegisterNewUser(),
    [onRegisterNewUser]
  );
  const handleSubmit = async (
    values: LoginFormValues,
    formikHelpers: FormikHelpers<LoginFormValues>
  ) => {
    setSendResetPasswordStatus("none");
    const result = await identityDao.loginUsingCookies({
      email: values.username,
      password: values.password,
    });
    formikHelpers.setSubmitting(false);
    onLoginStateChange(result);
  };

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit}>
      {(formik) => {
        return (
          <Form id={"login-form"} name={"login"} autoComplete={"on"}>
            <Stack useFlexGap sx={{ gap: 2 }}>
              <PropertiesCard
                header={{
                  title: t("Log In.title"),
                }}
                sx={{
                  justifyContent: "center",
                }}
                elevation={width === SizeClass.Compact ? 0 : undefined}
              >
                <FormikProperty
                  formik={formik}
                  name={"username"}
                  label={t("Email")}
                  mode={"edit"}
                  autoComplete={"username"}
                  fullWidth
                  autoFocus
                  inputProps={{
                    id: "username",
                    inputMode: "email",
                    autoCapitalize: "none",
                    autoCorrect: "off",
                    spellCheck: false,
                  }}
                />
                <FormikProperty
                  formik={formik}
                  name={"password"}
                  label={t("Password")}
                  type={"password"}
                  autoComplete={"current-password"}
                  mode={"edit"}
                  fullWidth
                  inputProps={{
                    id: "password",
                    autoCapitalize: "none",
                    autoCorrect: "off",
                    spellCheck: false,
                  }}
                />
                <Stack
                  direction={"row"}
                  sx={{ width: "100%", justifyContent: "space-evenly" }}
                >
                  <Button
                    type={"submit"}
                    color={"secondary"}
                    variant={"contained"}
                    disabled={!formik.dirty}
                    loading={formik.isSubmitting}
                    onClick={formik.submitForm}
                  >
                    {t("Log In")}
                  </Button>
                </Stack>
              </PropertiesCard>
              {loginState === "UnauthorizedLoginAttempt" &&
                sendResetPasswordStatus !== "sent" && (
                  <Alert severity={"warning"}>
                    {t("Wrong username or password")}
                  </Alert>
                )}
              <LoginFooter
                username={formik.values.username}
                loginStrategy={strategy}
                onNewUser={handleNewUserClick}
              />
            </Stack>
          </Form>
        );
      }}
    </Formik>
  );
};
